import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
    Users, 
    Mail, 
    ShoppingBag, 
    TrendingUp, 
    Download,
    Calendar,
    Package,
    DollarSign,
    RefreshCw,
    Lock,
    Eye,
    EyeOff
} from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Admin password - in production this should be environment variable
const ADMIN_PASSWORD = 'RawMinerals2026';

const AdminPage = () => {
    const { t } = useLanguage();
    const { user } = useAuth();
    const [loading, setLoading] = useState(true);
    const [overview, setOverview] = useState(null);
    const [salesStats, setSalesStats] = useState(null);
    const [activeTab, setActiveTab] = useState('overview');
    
    // Auth state
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [authError, setAuthError] = useState('');

    useEffect(() => {
        // Check if admin is already authenticated in this session
        const adminAuth = sessionStorage.getItem('adminAuthenticated');
        if (adminAuth === 'true') {
            setIsAuthenticated(true);
            fetchData();
        } else {
            setLoading(false);
        }
    }, []);

    const handleAdminLogin = (e) => {
        e.preventDefault();
        if (password === ADMIN_PASSWORD) {
            sessionStorage.setItem('adminAuthenticated', 'true');
            setIsAuthenticated(true);
            setAuthError('');
            fetchData();
        } else {
            setAuthError('Feil passord');
        }
    };

    const handleAdminLogout = () => {
        sessionStorage.removeItem('adminAuthenticated');
        setIsAuthenticated(false);
        setPassword('');
    };

    const fetchData = async () => {
        setLoading(true);
        try {
            const [overviewRes, salesRes] = await Promise.all([
                fetch(`${API_URL}/api/admin/stats/overview`),
                fetch(`${API_URL}/api/admin/stats/sales`)
            ]);
            
            const overviewData = await overviewRes.json();
            const salesData = await salesRes.json();
            
            setOverview(overviewData);
            setSalesStats(salesData);
        } catch (error) {
            console.error('Error fetching admin data:', error);
        } finally {
            setLoading(false);
        }
    };

    const exportSubscribersCSV = () => {
        if (!overview?.subscribers?.recent) return;
        
        const headers = ['E-post', 'Navn', 'Påmeldt dato'];
        const rows = overview.subscribers.recent.map(sub => [
            sub.email,
            sub.name || '',
            new Date(sub.subscribed_at).toLocaleDateString('no-NO')
        ]);
        
        const csv = [headers, ...rows].map(row => row.join(',')).join('\n');
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `nyhetsbrev-abonnenter-${new Date().toISOString().split('T')[0]}.csv`;
        link.click();
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('no-NO', {
            style: 'currency',
            currency: 'NOK',
            minimumFractionDigits: 0
        }).format(amount);
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('no-NO', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const formatMonth = (monthKey) => {
        const [year, month] = monthKey.split('-');
        const date = new Date(year, parseInt(month) - 1);
        return date.toLocaleDateString('no-NO', { year: 'numeric', month: 'long' });
    };

    if (loading && !isAuthenticated) {
        return (
            <div className="min-h-screen bg-stone-100 pt-32 pb-20">
                <div className="max-w-7xl mx-auto px-6">
                    <div className="flex items-center justify-center h-64">
                        <RefreshCw className="w-8 h-8 animate-spin text-stone-400" />
                    </div>
                </div>
            </div>
        );
    }

    // Admin login screen
    if (!isAuthenticated) {
        return (
            <div className="min-h-screen bg-stone-100 pt-32 pb-20">
                <div className="max-w-md mx-auto px-6">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="bg-white p-8 shadow-sm"
                    >
                        <div className="text-center mb-8">
                            <div className="w-16 h-16 bg-stone-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                <Lock className="w-8 h-8 text-stone-600" />
                            </div>
                            <h1 className="font-display text-2xl font-semibold text-stone-900 mb-2">
                                Admin Dashboard
                            </h1>
                            <p className="text-stone-500 text-sm">
                                Logg inn for å få tilgang
                            </p>
                        </div>

                        <form onSubmit={handleAdminLogin} className="space-y-4">
                            <div className="relative">
                                <Input
                                    type={showPassword ? 'text' : 'password'}
                                    placeholder="Admin-passord"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    className="rounded-none h-12 pr-12"
                                    data-testid="admin-password"
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPassword(!showPassword)}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600"
                                >
                                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                                </button>
                            </div>

                            {authError && (
                                <p className="text-red-500 text-sm text-center">{authError}</p>
                            )}

                            <Button
                                type="submit"
                                className="w-full rounded-none h-12 bg-stone-900 hover:bg-stone-800"
                                data-testid="admin-login-btn"
                            >
                                Logg inn
                            </Button>
                        </form>
                    </motion.div>
                </div>
            </div>
        );
    }

    if (loading) {
        return (
            <div className="min-h-screen bg-stone-100 pt-32 pb-20">
                <div className="max-w-7xl mx-auto px-6">
                    <div className="flex items-center justify-center h-64">
                        <RefreshCw className="w-8 h-8 animate-spin text-stone-400" />
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-stone-100 pt-32 pb-20">
            <div className="max-w-7xl mx-auto px-6">
                {/* Header */}
                <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8">
                    <div>
                        <h1 className="font-display text-3xl md:text-4xl font-semibold text-stone-900 mb-2">
                            Admin Dashboard
                        </h1>
                        <p className="text-stone-600">Oversikt over nyhetsbrev og salg</p>
                    </div>
                    <div className="flex gap-3 mt-4 md:mt-0">
                        <Button 
                            onClick={fetchData}
                            variant="outline"
                            className="rounded-none"
                        >
                            <RefreshCw className="w-4 h-4 mr-2" />
                            Oppdater
                        </Button>
                        <Button 
                            onClick={handleAdminLogout}
                            variant="outline"
                            className="rounded-none text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                            <Lock className="w-4 h-4 mr-2" />
                            Logg ut
                        </Button>
                    </div>
                </div>

                {/* Stats Cards */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="bg-white p-6 shadow-sm"
                    >
                        <div className="flex items-center gap-3 mb-3">
                            <Mail className="w-5 h-5 text-stone-500" />
                            <span className="text-sm text-stone-500 uppercase tracking-wider">Abonnenter</span>
                        </div>
                        <p className="text-3xl font-display font-semibold">{overview?.subscribers?.total || 0}</p>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.1 }}
                        className="bg-white p-6 shadow-sm"
                    >
                        <div className="flex items-center gap-3 mb-3">
                            <ShoppingBag className="w-5 h-5 text-stone-500" />
                            <span className="text-sm text-stone-500 uppercase tracking-wider">Ordrer</span>
                        </div>
                        <p className="text-3xl font-display font-semibold">{overview?.orders?.completed || 0}</p>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.2 }}
                        className="bg-white p-6 shadow-sm"
                    >
                        <div className="flex items-center gap-3 mb-3">
                            <Users className="w-5 h-5 text-stone-500" />
                            <span className="text-sm text-stone-500 uppercase tracking-wider">Brukere</span>
                        </div>
                        <p className="text-3xl font-display font-semibold">{overview?.users?.total || 0}</p>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.3 }}
                        className="bg-white p-6 shadow-sm"
                    >
                        <div className="flex items-center gap-3 mb-3">
                            <TrendingUp className="w-5 h-5 text-stone-500" />
                            <span className="text-sm text-stone-500 uppercase tracking-wider">Omsetning</span>
                        </div>
                        <p className="text-3xl font-display font-semibold">
                            {formatCurrency(salesStats?.total_revenue || 0)}
                        </p>
                    </motion.div>
                </div>

                {/* Tabs */}
                <div className="flex gap-4 mb-6 border-b border-stone-200">
                    <button
                        onClick={() => setActiveTab('overview')}
                        className={`pb-3 px-2 text-sm font-medium transition-colors ${
                            activeTab === 'overview' 
                                ? 'text-stone-900 border-b-2 border-stone-900' 
                                : 'text-stone-500 hover:text-stone-700'
                        }`}
                    >
                        Oversikt
                    </button>
                    <button
                        onClick={() => setActiveTab('subscribers')}
                        className={`pb-3 px-2 text-sm font-medium transition-colors ${
                            activeTab === 'subscribers' 
                                ? 'text-stone-900 border-b-2 border-stone-900' 
                                : 'text-stone-500 hover:text-stone-700'
                        }`}
                    >
                        Nyhetsbrev-abonnenter
                    </button>
                    <button
                        onClick={() => setActiveTab('sales')}
                        className={`pb-3 px-2 text-sm font-medium transition-colors ${
                            activeTab === 'sales' 
                                ? 'text-stone-900 border-b-2 border-stone-900' 
                                : 'text-stone-500 hover:text-stone-700'
                        }`}
                    >
                        Salgsstatistikk
                    </button>
                </div>

                {/* Tab Content */}
                {activeTab === 'overview' && (
                    <div className="grid md:grid-cols-2 gap-6">
                        {/* Recent Subscribers */}
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            className="bg-white p-6 shadow-sm"
                        >
                            <div className="flex items-center justify-between mb-4">
                                <h3 className="font-display text-lg font-semibold">Nyeste abonnenter</h3>
                                <Mail className="w-5 h-5 text-stone-400" />
                            </div>
                            <div className="space-y-3">
                                {overview?.subscribers?.recent?.slice(0, 5).map((sub, idx) => (
                                    <div key={idx} className="flex items-center justify-between py-2 border-b border-stone-100 last:border-0">
                                        <div>
                                            <p className="font-medium text-sm">{sub.email}</p>
                                            {sub.name && <p className="text-xs text-stone-500">{sub.name}</p>}
                                        </div>
                                        <span className="text-xs text-stone-400">
                                            {formatDate(sub.subscribed_at)}
                                        </span>
                                    </div>
                                )) || <p className="text-stone-500 text-sm">Ingen abonnenter ennå</p>}
                            </div>
                        </motion.div>

                        {/* Recent Orders */}
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ delay: 0.1 }}
                            className="bg-white p-6 shadow-sm"
                        >
                            <div className="flex items-center justify-between mb-4">
                                <h3 className="font-display text-lg font-semibold">Nyeste ordrer</h3>
                                <ShoppingBag className="w-5 h-5 text-stone-400" />
                            </div>
                            <div className="space-y-3">
                                {overview?.orders?.recent?.slice(0, 5).map((order, idx) => (
                                    <div key={idx} className="flex items-center justify-between py-2 border-b border-stone-100 last:border-0">
                                        <div>
                                            <p className="font-medium text-sm">{formatCurrency(order.total)}</p>
                                            <p className="text-xs text-stone-500">
                                                {order.items?.length || 0} produkt(er)
                                            </p>
                                        </div>
                                        <div className="text-right">
                                            <span className={`text-xs px-2 py-1 rounded ${
                                                order.payment_status === 'completed' 
                                                    ? 'bg-green-100 text-green-700'
                                                    : 'bg-yellow-100 text-yellow-700'
                                            }`}>
                                                {order.payment_status === 'completed' ? 'Betalt' : 'Venter'}
                                            </span>
                                        </div>
                                    </div>
                                )) || <p className="text-stone-500 text-sm">Ingen ordrer ennå</p>}
                            </div>
                        </motion.div>
                    </div>
                )}

                {activeTab === 'subscribers' && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        className="bg-white p-6 shadow-sm"
                    >
                        <div className="flex items-center justify-between mb-6">
                            <h3 className="font-display text-lg font-semibold">
                                Alle abonnenter ({overview?.subscribers?.total || 0})
                            </h3>
                            <Button 
                                onClick={exportSubscribersCSV}
                                variant="outline"
                                size="sm"
                                className="rounded-none"
                            >
                                <Download className="w-4 h-4 mr-2" />
                                Eksporter CSV
                            </Button>
                        </div>
                        
                        <div className="overflow-x-auto">
                            <table className="w-full">
                                <thead>
                                    <tr className="border-b border-stone-200">
                                        <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-stone-500 font-medium">E-post</th>
                                        <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-stone-500 font-medium">Navn</th>
                                        <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-stone-500 font-medium">Påmeldt</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {overview?.subscribers?.recent?.map((sub, idx) => (
                                        <tr key={idx} className="border-b border-stone-100 hover:bg-stone-50">
                                            <td className="py-3 px-4 text-sm">{sub.email}</td>
                                            <td className="py-3 px-4 text-sm text-stone-600">{sub.name || '-'}</td>
                                            <td className="py-3 px-4 text-sm text-stone-500">
                                                {formatDate(sub.subscribed_at)}
                                            </td>
                                        </tr>
                                    )) || (
                                        <tr>
                                            <td colSpan="3" className="py-8 text-center text-stone-500">
                                                Ingen abonnenter ennå
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </motion.div>
                )}

                {activeTab === 'sales' && (
                    <div className="space-y-6">
                        {/* Product Totals */}
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            className="bg-white p-6 shadow-sm"
                        >
                            <div className="flex items-center gap-3 mb-6">
                                <Package className="w-5 h-5 text-stone-500" />
                                <h3 className="font-display text-lg font-semibold">Salg per produkt (totalt)</h3>
                            </div>
                            
                            {salesStats?.product_totals && Object.keys(salesStats.product_totals).length > 0 ? (
                                <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
                                    {Object.entries(salesStats.product_totals).map(([product, stats]) => (
                                        <div key={product} className="p-4 bg-stone-50 rounded">
                                            <h4 className="font-medium text-sm mb-2">{product}</h4>
                                            <div className="flex justify-between text-sm">
                                                <span className="text-stone-500">Antall:</span>
                                                <span className="font-medium">{stats.quantity}</span>
                                            </div>
                                            <div className="flex justify-between text-sm">
                                                <span className="text-stone-500">Omsetning:</span>
                                                <span className="font-medium">{formatCurrency(stats.revenue)}</span>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <p className="text-stone-500 text-sm">Ingen salgsdata ennå</p>
                            )}
                        </motion.div>

                        {/* Monthly Sales */}
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ delay: 0.1 }}
                            className="bg-white p-6 shadow-sm"
                        >
                            <div className="flex items-center gap-3 mb-6">
                                <Calendar className="w-5 h-5 text-stone-500" />
                                <h3 className="font-display text-lg font-semibold">Salg per måned</h3>
                            </div>
                            
                            {salesStats?.monthly_sales && salesStats.monthly_sales.length > 0 ? (
                                <div className="space-y-6">
                                    {salesStats.monthly_sales.map((month) => (
                                        <div key={month.month} className="border-b border-stone-100 pb-4 last:border-0">
                                            <h4 className="font-medium mb-3">{formatMonth(month.month)}</h4>
                                            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-3">
                                                {Object.entries(month.products).map(([product, stats]) => (
                                                    <div key={product} className="flex justify-between text-sm p-2 bg-stone-50 rounded">
                                                        <span>{product}</span>
                                                        <span className="font-medium">
                                                            {stats.quantity} stk / {formatCurrency(stats.revenue)}
                                                        </span>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <p className="text-stone-500 text-sm">Ingen månedlige salgsdata ennå</p>
                            )}
                        </motion.div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default AdminPage;
